import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { getCertificate, updateCertificate } from "@/services/http/members";
import { Toast } from "@components/common";

import { CertificateActions, CertificateState, Item } from "./Certificate.types";

const itemsFont: Item[] = [
  { label: "Qwitcher Grypen", description: "Qwitcher Grypen Bold", value: "qwitcher-grypen" },
  { label: "Allison", description: "Allison Regular", value: "allison" },
  { label: "Ms Madi", description: "Ms Madi Regular", value: "ms-madi" },
  { label: "WindSong", description: "WindSong Medium", value: "wind-song" },
  { label: "Halimun", description: "Halimun Regular", value: "halimun" },
];

export const defaultState: CertificateState = {
  content: `
  <p>CERTIFICADO DE CONCLUSÃO</p>
  <h1><strong><span style="color: #0E6EDE">[Nome do Curso]</span></strong></h1>
  <br/>
  <p>Certificamos que</p>
  <h2>[Nome do Aluno]</h2>
  <p id="course-completed">Completou o curso [Nome do Curso] com sucesso em [Data],</p>
  <p id="course-hours">com carga horária de [Horas do Curso] hora(s).</p>
  `,
  primaryColor: "#000",
  secondaryColor: "#666",
  thirdColor: "#999",
};

const initialState: CertificateState = {
  isLoading: false,
  enableEditing: false,
  activeCertificate: false,
  activeLogo: false,
  activeCourseDuration: false,
  activeSignature: false,
  courseDuration: "",
  signatureFont: "qwitcher-grypen",
  signatureFontName: "Qwitcher Grypen",
  orientation: undefined,
  uuid: "",
  signature: "",
  isEditing: false,
  isEditingContent: false,
  isDownload: false,
  logo: "",
  itemsFont,
  primaryColorDraft: "",
  secondaryColorDraft: "",
  thirdColorDraft: "",
  course: {},
  ...defaultState,
};

export const useCertificateStore = create<CertificateActions & CertificateState>()(
  devtools((set, get) => ({
    ...initialState,

    setIsLoading: state => set({ isLoading: state }),

    updateCertificate: async (uuid, config) => {
      const configOld = get();
      set(state => ({ ...state, ...config }));
      try {
        await updateCertificate({ uuid, ...config });
      } catch (error) {
        set(state => ({ ...state, ...configOld }));
        Toast.error("Error", "Erro atualizar certificado");
      }
    },

    updateSignatureFontName: () => {
      const { signatureFont } = get();

      const find = itemsFont.find(item => item.value === signatureFont);
      const signatureFontName = find ? find.label : "Rusty Cellair";

      set({ signatureFontName });
    },

    updateStore: config => {
      const configOld = get();
      set({ ...configOld, ...config });
    },

    updateThemeColorDraft: () => {
      set(state => {
        return {
          ...state,
          primaryColorDraft: state.primaryColor,
          secondaryColorDraft: state.secondaryColor,
          thirdColorDraft: state.thirdColor,
        };
      });
    },

    updateThemeColorDraftRevert: () => {
      set(state => {
        return {
          ...state,
          primaryColor: state.primaryColorDraft,
          secondaryColor: state.secondaryColorDraft,
          thirdColor: state.thirdColorDraft,
        };
      });
    },

    restoreEditor: uuid => {
      const { updateCertificate, course, courseDuration } = get();
      const certificate = {
        ...defaultState,
        content: defaultState.content
          ?.replaceAll("[Nome do Curso]", course?.name ?? "")
          .replaceAll("[Horas do Curso]", courseDuration ?? ""),
      };
      updateCertificate(uuid, certificate);
    },

    changeStyleCourseDuration: async (uuid, activeCourseDuration) => {
      const { updateStore, updateCertificate, content } = get();
      let parser = new DOMParser();
      let doc = parser.parseFromString(content ?? "", "text/html");
      let element = doc.getElementById("course-hours");
      if (element) {
        if (activeCourseDuration) {
          element.classList.add("block");
          element.classList.remove("hidden");
        } else {
          element.classList.add("hidden");
          element.classList.remove("block");
        }
      }
      let newContent = doc.body.innerHTML;
      await updateStore({ isEditingContent: true });
      await updateCertificate(uuid, { content: newContent });
      await updateStore({ isEditingContent: false });
    },

    changeCourseCompleted: async (uuid, activeCourseDuration) => {
      const { updateStore, updateCertificate, content } = get();
      let parser = new DOMParser();
      let doc = parser.parseFromString(content ?? "", "text/html");
      let element = doc.getElementById("course-completed");
      if (element && element.textContent) {
        let newText = element.textContent;
        if (activeCourseDuration) {
          newText = element.textContent?.replace(".", ",");
        } else {
          newText = element.textContent?.replace(",", ".");
        }
        element.textContent = newText;
      }
      let newContent = doc.body.innerHTML;
      await updateStore({ isEditingContent: true });
      await updateCertificate(uuid, { content: newContent });
      await updateStore({ isEditingContent: false });
    },

    fetchCertificateData: async uuid => {
      const { updateSignatureFontName, updateThemeColorDraft, changeStyleCourseDuration, changeCourseCompleted } =
        get();
      set({ isLoading: true });
      try {
        const { data: res } = await getCertificate({ uuid });

        if (!res.content) {
          res.content = defaultState.content
            ?.replaceAll("[Nome do Curso]", res.course?.name ?? "")
            .replaceAll("[Horas do Curso]", res.courseDuration ?? "");
        }
        if (res.signature) res.signature = res.signature.substring(0, 30);
        if (res.courseDuration) res.courseDuration = res.courseDuration.substring(0, 3);
        set({ ...res });
        updateThemeColorDraft();
        updateSignatureFontName();
        changeStyleCourseDuration(uuid, res.activeCourseDuration ?? false);
        changeCourseCompleted(uuid, res.activeCourseDuration ?? false);
      } catch (error) {
        Toast.error("Error", "Erro carregar certificado");
      } finally {
        set({ isLoading: false });
      }
    },
  }))
);
